export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

export interface TelegramWebAppAPI {
  initData: string;
  initDataUnsafe: {
    query_id?: string;
    user?: TelegramUser;
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  isClosingConfirmationEnabled: boolean;
  expand: () => void;
  close: () => void;
  ready: () => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  enableClosingConfirmation: () => void;
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebAppAPI;
    };
  }
}

export function getTelegramWebApp(): TelegramWebAppAPI | null {
  if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
    return window.Telegram.WebApp;
  }
  return null;
}

export function initTelegramApp(): {
  isInsideTelegram: boolean;
  user?: TelegramUser;
  initData?: string;
} {
  const tg = getTelegramWebApp();

  if (tg && tg.initData) {
    try {
      tg.ready();
      tg.expand();
      if (typeof (tg as any).disableVerticalSwipes === 'function') {
        (tg as any).disableVerticalSwipes();
      }
      tg.setHeaderColor('#050811');
      tg.setBackgroundColor('#050811');
      tg.enableClosingConfirmation();
      return {
        isInsideTelegram: true,
        user: tg.initDataUnsafe?.user,
        initData: tg.initData,
      };
    } catch (e) {
      console.warn('Failed to fully initialize Telegram WebApp:', e);
    }
  }

  return {
    isInsideTelegram: false,
  };
}

export function triggerHaptic(type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'selection') {
  const tg = getTelegramWebApp();
  if (!tg?.HapticFeedback) return;

  if (type === 'success' || type === 'error') {
    tg.HapticFeedback.notificationOccurred(type);
  } else if (type === 'selection') {
    tg.HapticFeedback.selectionChanged();
  } else {
    tg.HapticFeedback.impactOccurred(type);
  }
}

export const telegramHaptics = {
  impact: (style: 'light' | 'medium' | 'heavy' = 'light') => triggerHaptic(style),
  notification: (type: 'success' | 'error') => triggerHaptic(type),
  selection: () => triggerHaptic('selection'),
};

export function openTelegramStarsInvoice(
  invoiceLink: string,
  onStatusChange?: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void
): Promise<'paid' | 'cancelled' | 'failed' | 'pending'> {
  return new Promise((resolve) => {
    const tg = getTelegramWebApp();
    if (tg && typeof tg.openInvoice === 'function') {
      tg.openInvoice(invoiceLink, (status: string) => {
        const mapped = (status as any) || 'cancelled';
        if (onStatusChange) onStatusChange(mapped);
        resolve(mapped);
      });
    } else {
      console.log('[Telegram Stars] Outside Telegram WebApp, opening link:', invoiceLink);
      if (invoiceLink.startsWith('http')) {
        window.open(invoiceLink, '_blank');
      }
      resolve('pending');
    }
  });
}
